/**
 * Indicador de status de mensagem (enviando, enviada, entregue, lida, falha)
 * 
 * Para status 'failed', o ícone é clicável e abre dialog com detalhes do erro
 * se hasErrorDetails=true e onErrorClick for fornecido
 */

import { Check, CheckCheck, Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Database } from '@/integrations/supabase/types';

type MessageStatus = Database['public']['Enums']['message_status'];

interface MessageStatusIndicatorProps {
  status: MessageStatus;
  direction: 'inbound' | 'outbound';
  className?: string;
  /** Callback quando clica no ícone de erro (status='failed') */
  onErrorClick?: () => void;
  /** Indica se há detalhes de erro disponíveis (habilita clique) */
  hasErrorDetails?: boolean;
}

export function MessageStatusIndicator({ 
  status, 
  direction, 
  className,
  onErrorClick,
  hasErrorDetails,
}: MessageStatusIndicatorProps) {
  // Só exibe indicador para mensagens outbound
  if (direction === 'inbound') {
    return null;
  }

  // Retorna o ícone apropriado baseado no status da mensagem
  const getStatusIcon = () => {
    switch (status) {
      case 'pending':
        return <Clock className={cn('h-3 w-3', className)} />;
      case 'sent':
        return <Check className={cn('h-3 w-3', className)} />;
      case 'delivered':
        return <CheckCheck className={cn('h-3 w-3', className)} />;
      case 'read':
        // NÃO aplica className externa para preservar a cor azul de "lido"
        return <CheckCheck className="h-3 w-3 text-blue-400" />;
      case 'failed':
        // Ícone de erro é clicável se houver detalhes disponíveis
        if (onErrorClick && hasErrorDetails) {
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onErrorClick();
              }}
              className={cn(
                'cursor-pointer hover:scale-125 transition-transform',
                'animate-pulse hover:animate-none'
              )}
              title="Clique para ver detalhes do erro"
            >
              <AlertCircle className="h-3 w-3 text-destructive" />
            </button>
          );
        }
        // NÃO aplica className externa para preservar a cor vermelha de "falha"
        return <AlertCircle className="h-3 w-3 text-destructive" />;
      default:
        return null;
    }
  };

  return (
    <span className="inline-flex items-center ml-1" title={getStatusTitle(status)}>
      {getStatusIcon()}
    </span>
  );
}

function getStatusTitle(status: MessageStatus): string {
  switch (status) {
    case 'pending':
      return 'Enviando...';
    case 'sent':
      return 'Enviada';
    case 'delivered':
      return 'Entregue';
    case 'read':
      return 'Lida';
    case 'failed':
      return 'Falha no envio';
    default:
      return '';
  }
}
